import { DirectionsCar, QrCodeRounded } from "@mui/icons-material";
import { Button as MuiButton, Chip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../utils/api"; // Import your API module here
import { memberVehicleLabel, isCoffeePassSubscription } from "../../utils/coffeePass";
import { subscriptionRequiresContract } from "../../utils/subscriptionContractGate";
import { getMembershipStatusPresentation } from "../../utils/subscriptionDisplayStatus";
import MySubscriptionDetails from "./MySubscriptionDetails";
import ModalPopup from "../../components/ModalPopup";
import styled from "@emotion/styled";
import StarIcon from "@mui/icons-material/Star";

const Button = styled(MuiButton)`
  border-radius: 28px;
  padding: 8px 12px;
`;

const CustomerSubscriptionCard = ({ subscription, isInactive = false, isMainSubscription = true }) => {
  function formatDate(dateString) {
    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = new Date(dateString).toLocaleDateString(undefined, options);
    return formattedDate;
  }

  const navigate = useNavigate();
  const [redemptionCount, setRedemptionCount] = useState(0);
  const [showSubscriptionDetails, setShowSubscriptionDetails] = useState(false);
  const [isMaxRedemptions, setIsMaxRedemptions] = useState(false);
  const [contractSigned, setContractSigned] = useState(false);

  // Check if this is an add-on subscription
  const isAddOn = subscription.note && subscription.note.mainSubscriptionId;
  const vehicleLabel = memberVehicleLabel(subscription);
  const isCoffeePass = isCoffeePassSubscription(subscription);

  // Check contract status for subscription-type products
  useEffect(() => {
    const checkContractStatus = async () => {
      // Only check for subscription-type products
      if (subscriptionRequiresContract(subscription.product, subscription)) {
        try {
          const contractResponse = await api(`v1/contract/subscription/${subscription._id}`, "GET");
          if (contractResponse.data.success) {
            const isSigned = contractResponse.data.data.isSigned;
            const requiresContract = contractResponse.data.data.requiresContract;
            setContractSigned(isSigned || !requiresContract);
          }
        } catch (error) {
          console.error("Error checking contract status:", error);
          // On error, assume contract is required but not signed (safer default)
          setContractSigned(false);
        }
      } else {
        // Non-subscription products don't require contracts
        setContractSigned(true);
      }
    };

    checkContractStatus();
  }, [subscription._id, subscription.product?.type]);

  useEffect(() => {
    const fetchRedemptionCount = async () => {
      try {
        const redemptionCount = await api(`v1/subscription/${subscription._id}/redemptions`, "GET");
        if (redemptionCount && redemptionCount.data.success) {
          const redemptions = redemptionCount.data.data.redemptions || [];
          const subVehicleId = subscription.vehicle?._id || subscription.vehicle;
          const filtered =
            subVehicleId == null
              ? redemptions
              : redemptions.filter((r) => {
                  const rVehicleId = r.vehicle?._id || r.vehicle;
                  return rVehicleId != null && String(rVehicleId) === String(subVehicleId);
                });
          const count = filtered.length;
          setRedemptionCount(count);

          // Check if max redemptions reached
          // "Fully Redeemed" should ONLY apply to:
          // 1. One-time products (onceoff)
          // 2. Add-on products (isAddOn === true)
          // Should NOT apply to main subscriptions (type === "subscription" && !isAddOn)

          let maxRedemptions = null;
          let shouldCheckMaxRedemptions = false;

          if (subscription.product.type === "onceoff") {
            // One-time products always have a limit of 1
            maxRedemptions = 1;
            shouldCheckMaxRedemptions = true;
          } else if (isAddOn) {
            // Add-on products: check if they have a packageCount limit
            if (subscription.product.packageCount && subscription.product.packageCount > 0) {
              maxRedemptions = subscription.product.packageCount;
              shouldCheckMaxRedemptions = true;
            }
            // If add-on has no packageCount, it's unlimited (don't check)
          } else if (subscription.product.type === "subscription") {
            // Main subscriptions: NEVER show "Fully Redeemed" regardless of packageCount
            // They are always unlimited from a display perspective
            shouldCheckMaxRedemptions = false;
          } else if (subscription.product.type === "package") {
            // Package products: check if they have a packageCount limit
            if (subscription.product.packageCount && subscription.product.packageCount > 0) {
              maxRedemptions = subscription.product.packageCount;
              shouldCheckMaxRedemptions = true;
            }
          }

          // Only set as max redemptions if we should check AND there's a limit that's been reached
          if (shouldCheckMaxRedemptions && maxRedemptions !== null && count >= maxRedemptions) {
            setIsMaxRedemptions(true);
          } else {
            setIsMaxRedemptions(false);
          }

          // If once off product, and has at least 1 redemption, set to inactive as already redeemed
          if (subscription.product.type == "onceoff" && count > 0) {
            subscription.active = false;
          }
        }
      } catch (error) {
        console.error("API error:", error);
      }
    };

    fetchRedemptionCount();
  }, [subscription._id, subscription.product.packageCount, subscription.product.type]);

  // Determine if card should be greyed out
  const shouldGreyOut = isMaxRedemptions;
  const statusPresentation = getMembershipStatusPresentation(subscription, {
    isInactiveTab: isInactive,
  });
  const cardClassName = `relative flex flex-col gap-4 justify-between shadow-lg pt-4 sm:pt-6 px-4 sm:px-6 pb-6 border rounded-2xl w-full max-w-[350px] ${
    shouldGreyOut ? "bg-gray-300 border-gray-400 opacity-60" : "bg-white border-gray-200"
  }`;

  return (
    <div className={cardClassName}>
      <div className="flex flex-col gap-3 flex-1">
        <div className="flex gap-4 items-center justify-center">
          <QrCodeRounded fontSize="large" className="bg-[#0F083E] text-white rounded-full p-1" />
          <div className="flex flex-col items-center gap-1">
            <h5 style={{ fontWeight: 700, margin: 0 }}>{subscription.product.name}</h5>
            {/* Show badge for add-on or main subscription */}
            {isAddOn ? (
              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium">Add-on</span>
            ) : (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                {isCoffeePass ? "Coffee Pass" : "Main Subscription"}
              </span>
            )}
            {/* Linked vehicle: full-width prominent row, rego only. Never a placeholder or a Coffee Pass. */}
            {vehicleLabel && (
              <div className="flex items-center gap-2 mt-3 w-full py-2 px-3 rounded-lg bg-gray-100">
                <DirectionsCar sx={{ fontSize: 20, color: "text.secondary" }} />
                <Typography variant="body2" fontWeight={500} sx={{ color: "text.primary" }}>
                  Linked to: {vehicleLabel}
                </Typography>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 items-center">
            <Chip
              className="rounded"
              sx={{ fontSize: "16px" }}
              label={
                subscriptionRequiresContract(subscription.product, subscription) &&
                subscription.active &&
                !contractSigned &&
                statusPresentation.statusLabel === "Active"
                  ? "Pending"
                  : statusPresentation.statusLabel
              }
              color={
                statusPresentation.statusLabel === "Payment required"
                  ? "error"
                  : statusPresentation.statusLabel === "Scheduled" ||
                      statusPresentation.statusLabel === "Pending cancellation"
                    ? "warning"
                    : statusPresentation.statusLabel === "Active"
                      ? "success"
                      : "default"
              }
            />
          </div>
          {statusPresentation.expirySuffix && (
            <div
              className={`text-xs ${statusPresentation.expiryMuted ? "text-amber-800" : "text-gray-600"}`}
            >
              {statusPresentation.expirySuffix}
            </div>
          )}
          {!statusPresentation.expirySuffix &&
            subscription.validUntilDateUTC &&
            subscription.active &&
            (() => {
              const expiryDate = new Date(subscription.validUntilDateUTC);
              const isValidDate =
                !isNaN(expiryDate.getTime()) && expiryDate.getFullYear() !== 1970;
              if (!isValidDate) return null;
              return (
                <div className="text-xs text-gray-600">
                  <span className="font-semibold">Renews through:</span>{" "}
                  {formatDate(subscription.validUntilDateUTC)}
                </div>
              );
            })()}
        </div>
        {/* Show redemption count - always show for subscriptions, only show for packages/onceoff if packageCount exists */}
        {(subscription.product.type === "subscription" ||
          (subscription.product.packageCount && subscription.product.packageCount > 0) ||
          subscription.product.type === "onceoff") && (
          <div className="flex items-center text-lg">
            <StarIcon fontSize="large" style={{ color: "#faaf00", verticalAlign: "middle" }} />
            &nbsp;
            {(() => {
              // Main subscriptions (type === "subscription" && !isAddOn) are ALWAYS unlimited
              // They should never show a limit, regardless of packageCount
              if (subscription.product.type === "subscription" && !isAddOn) {
                return `${redemptionCount} redemptions used`;
              }
              // Add-on subscriptions with a packageCount limit
              else if (
                subscription.product.type === "subscription" &&
                isAddOn &&
                subscription.product.packageCount &&
                subscription.product.packageCount > 0
              ) {
                return `${redemptionCount} of ${subscription.product.packageCount} redemptions used`;
              }
              // Add-on subscriptions without a limit (unlimited)
              else if (subscription.product.type === "subscription" && isAddOn) {
                return `${redemptionCount} redemptions used`;
              }
              // Package or onceoff products
              else {
                return `${redemptionCount} of ${subscription.product.packageCount || 1} redemptions used`;
              }
            })()}
          </div>
        )}
        {/* {subscription.product.type === 'package' && <Rating className="flex flex-wrap" name="read-only" value={redemptionCount} max={subscription.product.packageCount} readOnly />} */}

        <div className="flex-1 flex flex-col">
          <h6 className="text-sm font-semibold text-gray-700 mb-1">What's Included:</h6>
          <span className="text-sm leading-relaxed text-gray-600">{subscription.product.description}</span>
        </div>
      </div>
      <div className="subscription-card-actions w-full flex justify-end gap-2 mt-auto">
        {/* Only show View button if subscription is not inactive with future validFromDateUTC */}
        {!(isInactive && subscription.validFromDateUTC) && (
          <Button
            className="rounded"
            variant="contained"
            color="secondary"
            onClick={() => {
              setShowSubscriptionDetails(true);
            }}
          >
            View
          </Button>
        )}
        <ModalPopup
          showModal={showSubscriptionDetails}
          handleShowModal={() => setShowSubscriptionDetails(false)}
          popupTitle=""
          popupContent={
            <MySubscriptionDetails
              subscription={subscription}
              onClose={() => {
                setShowSubscriptionDetails(false);
              }}
              callback={() => {
                setShowSubscriptionDetails(false);
              }}
              onRefresh={async () => {
                if (!subscriptionRequiresContract(subscription.product, subscription)) return;
                try {
                  const contractResponse = await api(`v1/contract/subscription/${subscription._id}`, "GET");
                  if (contractResponse.data.success) {
                    const isSigned = contractResponse.data.data.isSigned;
                    const requiresContract = contractResponse.data.data.requiresContract;
                    setContractSigned(isSigned || !requiresContract);
                  }
                } catch (e) {
                  console.error("Error refreshing contract status on card:", e);
                }
              }}
            />
          }
        />
      </div>

      {/* Overlay for inactive subscriptions with future validFromDateUTC */}
      {isInactive && subscription.validFromDateUTC && (
        <div className="absolute inset-0 bg-black/70 rounded-3xl flex items-center justify-center z-10">
          <div className="text-center text-white p-4">
            <div className="text-lg font-semibold mb-2">Coming Soon</div>
            <div className="text-sm">Available from</div>
            <div className="text-base font-medium">{formatDate(subscription.validFromDateUTC)}</div>
          </div>
        </div>
      )}

      {/* Overlay for max redemptions reached */}
      {shouldGreyOut && (
        <div className="absolute inset-0 bg-gray-500/50 rounded-3xl flex items-center justify-center z-10">
          <div className="text-center text-white p-4">
            <div className="text-lg font-semibold mb-2">Fully Redeemed</div>
            <div className="text-sm">All redemptions have been used</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSubscriptionCard;
